import React, { useState } from 'react';
import { Alert, Dropdown, Spinner } from 'react-bootstrap';
import {
  useEticketingBusesQuery,
  useEticketingCreateBusMutation,
  useEticketingUpdateBusMutation,
  useEticketingDeleteBusMutation,
} from '../../../../api/e-ticketing/e-ticketing.hooks';
import { emojis } from '../../bootstrap/alertData';
import DeleteModal from '../../DeleteModal';

const Bus = () => {
  const { data: buses, isLoading: loadingBuses, error: busesError, refetch } = useEticketingBusesQuery();
  const { create: createBus, isLoading: creating } = useEticketingCreateBusMutation();
  const { update: updateBus, isLoading: updating } = useEticketingUpdateBusMutation();
  const { remove: deleteBus, isLoading: deleting } = useEticketingDeleteBusMutation();

  const [showForm, setShowForm] = useState(false);
  const [editingBus, setEditingBus] = useState(null);
  const [formData, setFormData] = useState({ name: '' });
  const [alert, setAlert] = useState({ show: false, message: '', variant: '' });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [busToDelete, setBusToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(0);

  const itemsPerPage = 10;
  const isEditMode = !!editingBus;

  const showAlert = (message, variant = 'success') => {
    setAlert({ show: true, message, variant });
    setTimeout(() => setAlert({ show: false, message: '', variant: '' }), 3000);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleAddNew = () => {
    setEditingBus(null);
    setFormData({ name: '' });
    setShowForm(true);
  };

  const handleEdit = (bus) => {
    setEditingBus(bus);
    setFormData({ name: bus.name });
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingBus(null);
    setFormData({ name: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (isEditMode) {
        await updateBus(editingBus.id, formData);
        showAlert('Bus updated successfully!');
      } else {
        await createBus(formData);
        showAlert('Bus added successfully!');
      }
      await refetch();
      handleCancel();
    } catch (err) {
      showAlert(err?.message || `Failed to ${isEditMode ? 'update' : 'create'} bus.`, 'danger');
    }
  };

  const handleDeleteClick = (bus) => {
    setBusToDelete(bus);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!busToDelete) return;

    try {
      await deleteBus(busToDelete.id);
      showAlert('Bus deleted successfully!');
      await refetch();
      setShowDeleteModal(false);
      setBusToDelete(null);
    } catch (err) {
      showAlert(err?.message || 'Failed to delete bus.', 'danger');
    }
  };

  // Filter + Pagination
  const filteredBuses = buses?.filter((bus) =>
    bus.name?.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];
  const totalPages = Math.ceil(filteredBuses.length / itemsPerPage);
  const paginatedBuses = filteredBuses.slice(
    currentPage * itemsPerPage,
    (currentPage + 1) * itemsPerPage
  );

  return (
    <div className="col-12">
      {alert.show && (
        <Alert variant={alert.variant} dismissible onClose={() => setAlert({ show: false, message: '', variant: '' })}>
          {alert.variant === 'success' ? emojis.success : emojis.warning} {alert.message}
        </Alert>
      )}

      {showForm ? (
        // Add/Edit Form
        <div className="card">
          <div className="card-header">
            <h4 className="card-title">
              {isEditMode ? 'Edit Bus' : 'Add Bus'}
            </h4>
          </div>
          <div className="card-body">
            <form onSubmit={handleSubmit}>
              <div className="row">
                <div className="col-xl-6">
                  <div className="mb-3 row">
                    <label className="col-lg-4 col-form-label">
                      Bus Name<span className="text-danger">*</span>
                    </label>
                    <div className="col-lg-6">
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleFormChange}
                        className="form-control"
                        placeholder="Enter bus name (e.g., Coaster, Double Decker)"
                        required
                      />
                    </div>
                  </div>
                </div>
                <div className="mb-3 row">
                  <div className="col-lg-10 ms-auto">
                    <button type="submit" className="btn btn-secondary" disabled={creating || updating}>
                      {creating || updating ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          {isEditMode ? 'Updating...' : 'Creating...'}
                        </>
                      ) : (
                        'Save'
                      )}
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-primary ms-2"
                      onClick={handleCancel}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      ) : (
        // Listing Buses
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h4 className="card-title m-0">Buses</h4>
            <div className="d-flex gap-2">
              <input
                type="text"
                className="form-control"
                placeholder="Search by bus name"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(0);
                }}
              />
              <button
                className="btn btn-secondary"
                style={{ whiteSpace: "nowrap" }}
                onClick={handleAddNew}
              >
                Add Bus
              </button>
            </div>
          </div>

          <div className="card-body">
            {loadingBuses ? (
              <div className="d-flex justify-content-center align-items-center p-4">
                <Spinner animation="border" role="status" />
              </div>
            ) : busesError ? (
              <Alert variant="danger">Failed to load buses. Please try again.</Alert>
            ) : (
              <div className="table-responsive">
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Bus Name</th>
                      <th>Route</th>
                      <th>Created At</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedBuses.length > 0 ? (
                      [...paginatedBuses]
                        .sort((a, b) => a.id - b.id)
                        .map((bus, i) => (
                          <tr key={bus.id}>
                            <td>{currentPage * itemsPerPage + i + 1}</td>
                            <td>{bus.name || "--"}</td>
                            <td>
                              {bus.route_id ? (
                                <span>{bus?.route?.name}</span>
                              ) : (
                                <span>Not Assigned</span>
                              )}
                            </td>
                            <td>{new Date(bus.createdAt).toLocaleDateString()}</td>
                            <td>
                              <Dropdown>
                                <Dropdown.Toggle variant="link">
                                  &#x2022;&#x2022;&#x2022;
                                </Dropdown.Toggle>
                                <Dropdown.Menu align="end">
                                  <Dropdown.Item onClick={() => handleEdit(bus)}>
                                    Edit
                                  </Dropdown.Item>
                                  <Dropdown.Item onClick={() => handleDeleteClick(bus)}>
                                    Delete
                                  </Dropdown.Item>
                                </Dropdown.Menu>
                              </Dropdown>
                            </td>
                          </tr>
                        ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="text-center">
                          No buses found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
            {/* Pagination */}
            {!loadingBuses && filteredBuses.length > 0 && (
              <div className="d-flex justify-content-between align-items-center mt-3">
                <div>
                  Showing {currentPage * itemsPerPage + 1} to{" "}
                  {Math.min((currentPage + 1) * itemsPerPage, filteredBuses.length)}{" "}
                  of {filteredBuses.length} entries
                </div>
                <div>
                  <button
                    className="btn btn-sm btn-outline-primary me-2"
                    disabled={currentPage === 0}
                    onClick={() => setCurrentPage(currentPage - 1)}
                  >
                    Prev
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => (
                    <button
                      key={i}
                      className={`btn btn-sm border me-1 ${currentPage === i ? "btn-primary text-white" : ""}`}
                      onClick={() => setCurrentPage(i)}
                    >
                      {i + 1}
                    </button>
                  ))}
                  <button
                    className="btn btn-sm btn-outline-primary ms-2"
                    disabled={currentPage === totalPages - 1}
                    onClick={() => setCurrentPage(currentPage + 1)}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <DeleteModal
        show={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setBusToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Delete Bus"
        message={
          busToDelete?.route_id
            ? `Are you sure you want to delete the bus "${busToDelete?.name}"? This bus is currently assigned to Route ID: ${busToDelete.route_id}. This action cannot be undone.`
            : `Are you sure you want to delete the bus "${busToDelete?.name}"? This action cannot be undone.`
        }
        confirmText="Delete"
        isLoading={deleting}
        danger={true}
      />
    </div>
  );
};

export default Bus;